import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { UserFiltersComponent } from '../../Partials/user-filters/user-filters.component';
import { ChangeRootDialogComponent } from '../../Dialogs/change-root-dialog/change-root-dialog.component';
import { ImpersonateDialogComponent } from '../../Dialogs/impersonate-dialog/impersonate-dialog.component';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvPaginatorComponent } from '../../../../Shared/Components/av-paginator/av-paginator.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { UsersFacade } from '../../../../../Facades/Users/users.facade';
import { DATE_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import { UserFilters } from '../../../Interfaces/user-filters.interface';
import { ChangeRootDialogResult } from '../../../Interfaces/change-root-dialog.interface';
import { ImpersonateDialogResult } from '../../../Interfaces/impersonate-dialog.interface';
import { TenantUser } from '../../../../../Models/backoffice/tenant-user.model';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    DatePipe,
    RouterLink,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    UserFiltersComponent,
    AvEmptyStateComponent,
    AvPaginatorComponent,
    AvStatusChipComponent,
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class UsersComponent {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _dialog = inject(MatDialog);

  readonly facade = inject(UsersFacade);

  readonly dateFormat = DATE_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;

  private readonly _queryParams = toSignal(this._route.queryParamMap);

  readonly organizationLabel = computed<string>(
    () => this.facade.organization()?.displayName ?? this.facade.organizationId(),
  );

  constructor() {
    this._registerOrganizationParam();
  }

  applyFilters(filters: UserFilters): void {
    this.facade.applyFilters(filters);
  }

  clearOrganization(): void {
    this._router.navigate(['/users'], { queryParams: {} });
  }

  goToPage(page: number): void {
    this.facade.goToPage(page);
  }

  changePageSize(pageSize: number): void {
    this.facade.changePageSize(pageSize);
  }

  openChangeRoot(user: TenantUser): void {
    const reference = this._dialog.open(ChangeRootDialogComponent, {
      data: {
        userName: user.fullName,
        userEmail: user.email ?? this.unresolved,
        organizationName: this.organizationLabel(),
        ownsCoreSeat: user.ownsCoreSeat,
        hasEligibleCandidate: this.facade.hasEligibleRootCandidate(),
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: ChangeRootDialogResult) => this._applyRootChange(user, result));
  }

  openImpersonate(user: TenantUser): void {
    const reference = this._dialog.open(ImpersonateDialogComponent, {
      data: { userName: user.fullName, userEmail: user.email ?? this.unresolved },
    });

    reference
      .afterClosed()
      .subscribe((result?: ImpersonateDialogResult) => this._applyImpersonation(user, result));
  }

  private _registerOrganizationParam(): void {
    effect(() => {
      const organizationId = this._queryParams()?.get('organization_id') ?? '';

      this._applyOrganization(organizationId);
    });
  }

  private _applyOrganization(organizationId: string): void {
    this.facade.setOrganization(organizationId);
  }

  private _applyRootChange(user: TenantUser, result?: ChangeRootDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.changeRoot(user.id, result.override);
  }

  private _applyImpersonation(user: TenantUser, result?: ImpersonateDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.impersonate(user.id, result.reason);
  }
}
